import { useState, type ReactNode } from 'react';
import { Btn, Card, Field, SectionHead } from './ui/primitives';
import { CISA_JUSTIFICATIONS } from '../lib/cra';

/**
 * Batch "does not apply" editor (user request 2026-07-24). Declaring that a
 * vulnerability does NOT affect the product isn't a Q16 field — the CRA-conformant
 * form is a VEX `not_affected` statement + a structured CISA justification code, which
 * rides with the SRP package as the CSAF/CycloneDX VEX exchange documents. Pick ONE
 * shared code + reason and apply it to every selected KEV report; each incident is then
 * marked "Not applicable" and leaves the open list. Manager sign-off (KEV guard).
 */

export interface NotApplicableInput {
  justificationCode: string;
  justification: string;
  impactStatement?: string;
}

interface Props {
  /** How many Art.14 reports this will be written to. */
  count: number;
  busy: boolean;
  onApply: (data: NotApplicableInput) => void;
  onCancel: () => void;
  note?: ReactNode;
}

export function NotApplicablePanel({ count, busy, onApply, onCancel, note }: Props) {
  const [code, setCode] = useState('');
  const [justification, setJustification] = useState('');
  const [impact, setImpact] = useState('');

  const canApply = code !== '' && justification.trim().length >= 10;

  return (
    <Card data-testid="not-applicable-editor" style={{ padding: 16, marginTop: 8 }}>
      <SectionHead
        title={`Mark ${count} as not applicable`}
        aside={<span className="hint">writes a VEX not-affected statement to each</span>}
      />
      <p className="hint" style={{ margin: '6px 0 14px' }}>
        “Not applicable” isn’t a Q16 field — the CRA-conformant way to say a vulnerability doesn’t affect your
        product is a <b>VEX not-affected</b> statement with a <b>CISA justification code</b>. Pick one reason;
        it’s written to all {count} reports (they move to the <b>Archived</b> filter, labelled “Not applicable”). {note}
      </p>
      <div className="form" style={{ display: 'grid', gap: 12, maxWidth: 820 }}>
        <Field label="CISA justification" req hint="the structured reason that reaches the regulator (no default)">
          <select className="select" data-testid="na-code" value={code} onChange={(e) => setCode(e.target.value)} style={{ maxWidth: 520 }}>
            <option value="">Select a justification…</option>
            {CISA_JUSTIFICATIONS.map((j) => <option key={j.code} value={j.code}>{j.code} — {j.label}</option>)}
          </select>
        </Field>
        <Field label="Why it doesn’t apply" req hint="a named reason shared across these units (min 10 characters)">
          <textarea
            className="input" rows={3} data-testid="na-justification" value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="e.g. the vulnerable JNDI lookup is disabled at build time across this product line (log4j2.formatMsgNoLookups=true); the code path is unreachable."
          />
        </Field>
        <Field label="Impact statement" hint="optional — the evidence a reviewer/regulator can check">
          <textarea
            className="input" rows={2} data-testid="na-impact" value={impact}
            onChange={(e) => setImpact(e.target.value)}
            placeholder="e.g. verified in the build config (.config line 2847) and by a runtime probe on firmware ≥ 3.2."
          />
        </Field>
        <div className="form-actions" style={{ display: 'flex', gap: 8 }}>
          <Btn variant="primary" disabled={busy || !canApply} data-testid="na-apply"
            onClick={() => onApply({ justificationCode: code, justification: justification.trim(), ...(impact.trim() ? { impactStatement: impact.trim() } : {}) })}>
            {busy ? 'Applying…' : `Mark ${count} not applicable`}
          </Btn>
          <Btn disabled={busy} onClick={onCancel}>Cancel</Btn>
        </div>
      </div>
    </Card>
  );
}
